import { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Inner component that handles the typewriter animation
 */
function TypewriterText({ text, isStreaming, className }) {
  const [displayedLength, setDisplayedLength] = useState(0);
  const containerRef = useRef(null);
  
  // Typewriter effect - animate displaying characters one at a time
  useEffect(() => {
    if (!text || displayedLength >= text.length) return;
    
    // Typewriter speed: 3 characters at 10ms = ~300 chars/sec for readable typing effect
    const timer = setTimeout(() => {
      setDisplayedLength(prev => Math.min(prev + 3, text.length));
    }, 10);
    
    return () => clearTimeout(timer);
  }, [text, displayedLength]);

  // Auto-scroll to keep new text visible
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [displayedLength]);

  const displayedText = useMemo(() => {
    return text ? text.substring(0, displayedLength) : "";
  }, [text, displayedLength]);

  // Format text with styling for headers, bullets, citations
  const formattedContent = useMemo(() => {
    if (!displayedText) return null;
    
    // APA citation regex - matches (Author, Year) or (Author et al., Year) patterns
    const citationRegex = /\(([A-Za-z][^)]*(?:19|20)\d{2}[^)]*)\)/g;
    
    return displayedText.split('\n').map((line, i) => {
      // Style section headers (### or ##)
      if (line.startsWith('###') || line.startsWith('##')) {
        const cleanLine = line.replace(/^#+\s*/, '').replace(/\*\*/g, '');
        return (
          <h3 key={i} className="font-bold text-teal-700 dark:text-teal-400 text-base mt-4 mb-2 first:mt-0 border-b border-teal-200 dark:border-teal-800 pb-1">
            {cleanLine}
          </h3>
        );
      }
      
      // Bold headers with colons
      if (line.includes('**') && (line.includes(':') || line.startsWith('**'))) {
        const cleanLine = line.replace(/\*\*/g, '');
        return (
          <p key={i} className="font-semibold text-slate-800 dark:text-slate-200 mt-3 mb-1 text-sm">
            {cleanLine}
          </p>
        );
      }
      
      // Style bullet points
      if (line.trim().startsWith('-') || line.trim().startsWith('•') || line.trim().match(/^\*\s/)) {
        const bulletContent = line.replace(/^[\s\-•*]+/, '');
        return (
          <div key={i} className="flex gap-2 ml-2 mb-1">
            <span className="text-teal-500 mt-0.5 text-sm">•</span>
            <span 
              className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ 
                __html: bulletContent
                  .replace(citationRegex, '<span class="text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-blue-900/30 px-1 rounded">($1)</span>')
                  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
              }}
            />
          </div>
        );
      }
      
      // Numbered items
      if (/^\d+\./.test(line.trim())) {
        return (
          <p 
            key={i} 
            className="mb-2 font-medium text-slate-800 dark:text-slate-200 text-sm"
            dangerouslySetInnerHTML={{ 
              __html: line
                .replace(citationRegex, '<span class="text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-blue-900/30 px-1 rounded">($1)</span>')
                .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            }}
          />
        );
      }
      
      // Regular text with styled APA citations
      return line.trim() ? (
        <p 
          key={i} 
          className="mb-2 text-slate-700 dark:text-slate-300 leading-relaxed text-sm"
          dangerouslySetInnerHTML={{ 
            __html: line
              .replace(citationRegex, '<span class="text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-blue-900/30 px-1 rounded">($1)</span>')
              .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
          }}
        />
      ) : <div key={i} className="h-1" />;
    });
  }, [displayedText]);

  const isTyping = displayedLength < (text?.length || 0);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="text-slate-800 dark:text-slate-200 leading-relaxed font-mono">
        {formattedContent}
        {/* Typewriter cursor */}
        {(isStreaming || isTyping) && (
          <motion.span
            className="inline-block w-2 h-5 ml-0.5 bg-teal-500 dark:bg-teal-400 align-middle"
            animate={{ opacity: [1, 0, 1] }}
            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * StreamingText component - Fast typewriter effect for AI explanations
 * Uses key prop to reset animation when text changes
 */
export function StreamingText({ text, isStreaming = false, className = "" }) {
  // Use text length as part of key to reset animation on new content
  const key = text ? `text-${text.length}-${text.substring(0, 20)}` : "empty";
  
  return (
    <TypewriterText 
      key={key}
      text={text} 
      isStreaming={isStreaming} 
      className={className} 
    />
  );
}

/**
 * StreamingLoader - Compact loading indicator
 */
export function StreamingLoader({ message = "Generating AI explanation..." }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <motion.div
        className="flex gap-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full bg-teal-500"
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.5, 1, 0.5]
            }}
            transition={{ 
              duration: 0.5, 
              repeat: Infinity, 
              delay: i * 0.12,
              ease: "easeInOut"
            }}
          />
        ))}
      </motion.div>
      <span className="text-teal-700 dark:text-teal-300 font-medium text-sm">
        {message}
      </span>
    </div>
  );
}

export default StreamingText;
